import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { JSResourceForBackground } from '@atlassian/react-async';

import type { VariantId } from '../../types';
import type { PersonalisedEbmVariantResult } from './use-personalised-ebm-variant';
import type VariantResolverComponent from './variant-resolver';

export type {
	PersonalisedEbmVariantResult,
	PersonalisedVariantFallbackReason,
} from './use-personalised-ebm-variant';

const variantResolverResource = JSResourceForBackground<typeof VariantResolverComponent>(
	/* webpackChunkName: "@atlaskit-internal_edition-benefits-modal-personalised-variant-resolver" */ './variant-resolver',
	{ moduleId: 'edition-benefits-modal-personalised-variant-resolver' },
);

const PersonalisedVariantContext = createContext<PersonalisedEbmVariantResult | null>(null);

export type PersonalisedVariantBoundaryProps = {
	children: React.ReactNode;
	/** Variant rendered until — and if — a personalised one arrives. */
	fallbackVariantId: VariantId;
	/** Identifies the personalisation rule set evaluated in the post office template. */
	variantSelectorId: string;
};

export function PersonalisedVariantBoundary({
	children,
	fallbackVariantId,
	variantSelectorId,
}: PersonalisedVariantBoundaryProps): React.JSX.Element {
	const [Resolver, setResolver] = useState<typeof VariantResolverComponent | null>(null);
	const [resolved, setResolved] = useState<PersonalisedEbmVariantResult | null>(null);

	useEffect(
		() =>
			variantResolverResource.onComplete(
				// Wrapped in a callback: a bare component would be treated as a state updater function and invoked.
				(module) => setResolver(() => module),
				// A failed chunk load is not actionable here.
				// The fallback variant is already rendering, and `fallbackReason` stays `pending`, which consumers report.
				() => {},
			),
		[],
	);

	useEffect(() => {
		variantResolverResource.preload();
	}, []);

	const onResolved = useCallback((next: PersonalisedEbmVariantResult) => setResolved(next), []);

	const value = useMemo(
		(): PersonalisedEbmVariantResult =>
			resolved ?? {
				variantId: fallbackVariantId,
				isResolved: false,
				fallbackReason: 'pending',
			},
		[resolved, fallbackVariantId],
	);

	return (
		<PersonalisedVariantContext.Provider value={value}>
			{Resolver ? (
				<Resolver
					variantSelectorId={variantSelectorId}
					fallbackVariantId={fallbackVariantId}
					onResolved={onResolved}
				/>
			) : null}
			{children}
		</PersonalisedVariantContext.Provider>
	);
}

export function usePersonalisedVariantValue(): PersonalisedEbmVariantResult {
	const value = useContext(PersonalisedVariantContext);

	if (!value) {
		throw new Error(
			'usePersonalisedVariantValue must be called inside a PersonalisedVariantBoundary',
		);
	}

	return value;
}
